import { Platform } from 'react-native';
import { getApiUrl } from './apiConfig';
import { getSupabaseClient, getSupabaseProjectUrl, getTaoDocBucketName } from './supabase';
import { buildSupabasePublicObjectUrl } from './courseMediaUrls';

export const TAO2_FONT_SIZE_MIN = 14;
export const TAO2_FONT_SIZE_MAX = 32;
export const TAO2_FONT_SIZE_DEFAULT = 18;
export const TAO2_FONT_SIZE_STEP = 2;
export const TAO2_PREFS_KEY = '@kungfuapp/tao2ReaderPrefs';

/** Reader faces not used elsewhere in the app. Web loads Google Fonts; native uses system serifs. */
export const TAO2_FONTS = [
  {
    id: 'literata',
    label: 'Literata',
    web: 'Literata, Palatino, "Palatino Linotype", serif',
    native: Platform.OS === 'ios' ? 'Palatino' : 'serif',
  },
  {
    id: 'source-serif',
    label: 'Source Serif',
    web: '"Source Serif 4", Georgia, serif',
    native: Platform.OS === 'ios' ? 'Georgia' : 'serif',
  },
  {
    id: 'merriweather',
    label: 'Merriweather',
    web: 'Merriweather, "Iowan Old Style", serif',
    native: Platform.OS === 'ios' ? 'Times New Roman' : 'serif',
  },
  {
    id: 'libre-baskerville',
    label: 'Libre Baskerville',
    web: '"Libre Baskerville", "Times New Roman", serif',
    native: Platform.OS === 'ios' ? 'Times New Roman' : 'serif',
  },
  {
    id: 'newsreader',
    label: 'Newsreader',
    web: 'Newsreader, Palatino, serif',
    native: Platform.OS === 'ios' ? 'Palatino' : 'serif',
  },
];

export const TAO2_DEFAULT_FONT_ID = TAO2_FONTS[0].id;

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|tiff?)$/i;
const TOC_FILE_NAMES = new Set(['toc.json', 'contents.json', 'index.json']);
const TILE_NAME_RE = /^tao_tile_image(\.[^.]+)?$/i;
const TILE_OBJECT_CANDIDATES = [
  'tao_tile_image',
  'tao_tile_image.jpg',
  'tao_tile_image.jpeg',
  'tao_tile_image.png',
  'tao_tile_image.webp',
];
const GOOGLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Literata:ital,opsz,wght@0,7..72,400;0,7..72,600;0,7..72,700;1,7..72,400&family=Merriweather:ital,opsz,wght@0,18..144,300;0,18..144,400;0,18..144,700;1,18..144,400&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,600;0,6..72,700;1,6..72,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400&display=swap';

export function getTao2FontFamily(fontId) {
  const font = TAO2_FONTS.find((item) => item.id === fontId) || TAO2_FONTS[0];
  return Platform.OS === 'web' ? font.web : font.native;
}

/** The document renders as HTML (web iframe or native WebView), so always use the web stack. */
export function getTao2FontFamilyWeb(fontId) {
  const font = TAO2_FONTS.find((item) => item.id === fontId) || TAO2_FONTS[0];
  return font.web;
}

export function ensureTao2FontsLoaded() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  if (document.getElementById('tao2-reader-fonts')) return;
  const link = document.createElement('link');
  link.id = 'tao2-reader-fonts';
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_HREF;
  document.head.appendChild(link);
}

export function getTao2TileImageCandidates() {
  const projectUrl = getSupabaseProjectUrl();
  const bucket = getTaoDocBucketName();
  return TILE_OBJECT_CANDIDATES.map((objectPath) =>
    buildSupabasePublicObjectUrl(projectUrl, bucket, objectPath)
  ).filter(Boolean);
}

export function getTao2TileImageUrl() {
  return getTao2TileImageCandidates()[0] || '';
}

function naturalCompare(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function humanizeObjectName(path) {
  const base = String(path || '')
    .split('/')
    .pop()
    .replace(/\.[^.]+$/, '');
  const cleaned = base
    .replace(/[_-]+/g, ' ')
    .replace(/^\d+([.\s]+)/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || base || 'Page';
}

function slugify(value, used) {
  let base = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!base) base = 'section';
  let slug = base;
  let n = 2;
  while (used.has(slug)) {
    slug = `${base}-${n++}`;
  }
  used.add(slug);
  return slug;
}

function urlForObjectPath(objectPath) {
  return buildSupabasePublicObjectUrl(
    getSupabaseProjectUrl(),
    getTaoDocBucketName(),
    String(objectPath || '').replace(/^\/+/, '')
  );
}

function mapTocNodes(raw, used) {
  if (!Array.isArray(raw)) return [];
  return raw.map((node, index) => {
    if (typeof node === 'string') {
      const file = node;
      const title = humanizeObjectName(file);
      return {
        id: slugify(file || title || String(index), used),
        title,
        file,
        url: urlForObjectPath(file),
        children: [],
      };
    }
    const title = String(node?.title || node?.name || '').trim();
    const file = node?.file || node?.path || node?.image || null;
    const childRaw = node?.children || node?.subtitles || node?.sections || [];
    const children = mapTocNodes(Array.isArray(childRaw) ? childRaw : [], used);
    const resolvedFile = file || children[0]?.file || null;
    return {
      id: slugify(node?.id || resolvedFile || title || String(index), used),
      title: title || (resolvedFile ? humanizeObjectName(resolvedFile) : `Section ${index + 1}`),
      file: resolvedFile,
      url: resolvedFile ? urlForObjectPath(resolvedFile) : children[0]?.url || '',
      children,
    };
  });
}

function filesToToc(files) {
  const used = new Set();
  const groups = new Map();
  const sorted = [...files].sort((a, b) => naturalCompare(a.path, b.path));
  for (const file of sorted) {
    const parts = String(file.path).split('/');
    if (parts.length > 1) {
      const folder = parts[0];
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder).push(file);
    } else {
      groups.set(`__page__:${file.path}`, [file]);
    }
  }
  const toc = [];
  for (const [key, items] of groups) {
    if (key.startsWith('__page__:')) {
      const file = items[0];
      toc.push({
        id: slugify(file.path, used),
        title: humanizeObjectName(file.path),
        file: file.path,
        url: file.url || urlForObjectPath(file.path),
        children: [],
      });
      continue;
    }
    const children = items.map((file) => ({
      id: slugify(file.path, used),
      title: humanizeObjectName(file.path),
      file: file.path,
      url: file.url || urlForObjectPath(file.path),
      children: [],
    }));
    toc.push({
      id: slugify(key, used),
      title: humanizeObjectName(key),
      file: children[0]?.file || null,
      url: children[0]?.url || '',
      children: children.length > 1 ? children : [],
    });
  }
  return toc;
}

export function flattenTao2Pages(toc) {
  const pages = [];
  const seen = new Set();
  function walk(nodes) {
    for (const node of nodes || []) {
      if (node?.url && !seen.has(node.url)) {
        seen.add(node.url);
        pages.push({
          id: node.id,
          title: node.title,
          url: node.url,
          file: node.file || null,
        });
      }
      if (node?.children?.length) walk(node.children);
    }
  }
  walk(toc);
  return pages;
}

function isFolderEntry(item) {
  return !item || item.id == null;
}

async function listAllObjects(supabase, bucket, prefix = '') {
  const { data, error } = await supabase.storage.from(bucket).list(prefix, {
    limit: 1000,
    sortBy: { column: 'name', order: 'asc' },
  });
  if (error) throw new Error(error.message || 'Could not list tao_doc');
  const out = [];
  for (const item of data || []) {
    if (!item?.name || item.name === '.emptyFolderPlaceholder') continue;
    const path = prefix ? `${prefix}/${item.name}` : item.name;
    if (isFolderEntry(item)) {
      out.push(...(await listAllObjects(supabase, bucket, path)));
    } else {
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      out.push({ name: item.name, path, url: pub?.publicUrl || urlForObjectPath(path) });
    }
  }
  return out;
}

async function downloadJson(supabase, bucket, path) {
  const { data, error } = await supabase.storage.from(bucket).download(path);
  if (error || !data) return null;
  const text = await data.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function loadTao2ContentsFromStorage() {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Storage is not configured');
  const bucket = getTaoDocBucketName();
  const objects = await listAllObjects(supabase, bucket);
  const tile = objects.find((file) =>
    TILE_NAME_RE.test(String(file.name || '').split('/').pop() || '')
  );
  const tocFile = objects.find((file) => TOC_FILE_NAMES.has(String(file.name || '').toLowerCase()));
  let toc = [];
  if (tocFile) {
    const json = await downloadJson(supabase, bucket, tocFile.path);
    const raw = json?.toc || json?.sections || json?.contents || (Array.isArray(json) ? json : null);
    if (raw) toc = mapTocNodes(raw, new Set());
  }
  if (!toc.length) {
    const images = objects.filter((file) => {
      const name = String(file.name || '').split('/').pop() || '';
      if (TILE_NAME_RE.test(name) || TOC_FILE_NAMES.has(name.toLowerCase())) return false;
      return IMAGE_EXT.test(name) || !/\.[a-z0-9]+$/i.test(name);
    });
    toc = filesToToc(images);
  }
  return {
    tileUrl: tile?.url || getTao2TileImageUrl(),
    toc,
    pages: flattenTao2Pages(toc),
  };
}

export async function loadTao2Contents(viewerId) {
  const apiUrl = getApiUrl();
  const qs = viewerId != null ? `?viewer_id=${encodeURIComponent(String(viewerId))}` : '';
  try {
    const res = await fetch(`${apiUrl}/api/tao-2/contents${qs}`);
    if (res.ok) {
      const data = await res.json();
      const toc = Array.isArray(data?.toc) ? data.toc : [];
      return {
        tileUrl: data?.tileUrl || getTao2TileImageUrl(),
        toc,
        pages: Array.isArray(data?.pages) && data.pages.length ? data.pages : flattenTao2Pages(toc),
      };
    }
    if (res.status === 403) {
      const err = new Error('forbidden');
      err.code = 'forbidden';
      throw err;
    }
  } catch (error) {
    if (error?.code === 'forbidden') throw error;
  }
  return loadTao2ContentsFromStorage();
}
